import {
  Clock,
  Briefcase,
  AlertTriangle,
  UserX,
  FileCheck,
  ShieldAlert,
  DollarSign,
  Heart,
  Scale,
  FileText,
  Flame,
  Zap
} from 'lucide-react';
import { useState } from 'react';
import { Reveal } from './motion/Reveal';
import { StaggerGroup, StaggerItem } from './motion/Stagger';

export const ProblemTypes = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const allProblems = [
    // Demissão & Rescisão
    {
      icon: DollarSign,
      title: 'Demoraram mais de 10 dias pra pagar o acerto',
      description: 'A lei é clara: se passou de 10 dias da demissão, a empresa tem que te pagar mais um salário inteiro de multa.',
      category: 'rescisao',
    },
    {
      icon: Briefcase,
      title: 'Acerto veio pela metade ou parcelado',
      description: 'Você achou o valor muito baixo? Na maioria das vezes a empresa corta aviso prévio, férias e 13º de propósito.',
      category: 'rescisao',
    },
    {
      icon: UserX,
      title: 'Levei justa causa sem ter feito nada',
      description: 'Inventaram história pra não pagar seu FGTS e seguro-desemprego? A gente reverte isso e cobra tudo o que tiraram.',
      category: 'rescisao',
    },
    {
      icon: Scale,
      title: 'Quero sair porque não aguento mais abusos',
      description: 'Atrasam salário ou te humilham? Você tem o direito de sair da empresa recebendo tudo, igual se tivesse sido demitido.',
      category: 'rescisao',
    },
    {
      icon: DollarSign,
      title: 'FGTS sumiu ou nunca foi depositado',
      description: 'Abriu o aplicativo da Caixa e faltam meses ou anos? O patrão é obrigado a colocar cada centavo lá com juros.',
      category: 'rescisao',
    },

    // Horas Extras & Salários
    {
      icon: Clock,
      title: 'Horas extras que nunca foram pagas',
      description: 'Passava do horário todo dia e nunca viu a cor do dinheiro? Cobramos até os últimos 5 anos com juros e adicional.',
      category: 'horas',
    },
    {
      icon: FileCheck,
      title: 'Almoço corrido de 15 a 30 minutos',
      description: 'Engolia a comida rápido ou comia na frente do computador? A lei manda pagar esse horário como hora extra.',
      category: 'horas',
    },
    {
      icon: AlertTriangle,
      title: 'Banco de horas que nunca virava folga',
      description: 'Diziam que você ia folgar depois e a folga nunca chegava? A Justiça cancela isso e manda pagar tudo em dinheiro.',
      category: 'horas',
    },
    {
      icon: Zap,
      title: 'Chefe mandando mensagem de noite e folgas',
      description: 'Não podia desligar o celular e tinha que atender fora do horário? Isso conta como trabalho e tem que ser pago.',
      category: 'horas',
    },

    // Sem Carteira & MEI
    {
      icon: FileText,
      title: 'Trabalhou sem carteira assinada',
      description: 'Trabalhou igual todo mundo mas sem registro? Você tem direito a receber FGTS, 13º, férias e tempo pro INSS.',
      category: 'registro',
    },
    {
      icon: ShieldAlert,
      title: 'Obrigaram a abrir MEI ou virar PJ',
      description: 'Tinha horário pra entrar, chefe dando ordens e te fizeram emitir nota? É golpe da empresa. A lei manda pagar tudo de CLT.',
      category: 'registro',
    },

    // Insalubridade & Assédio
    {
      icon: Flame,
      title: 'Trabalho com barulho, sujeira ou risco',
      description: 'Trabalha com produtos químicos, lixo, barulho alto ou hospital? Você tem direito a receber até 40% a mais no salário.',
      category: 'adicionais',
    },
    {
      icon: AlertTriangle,
      title: 'Trabalho perigoso (moto, choque, vigia)',
      description: 'Arrisca a vida trabalhando de moto, com eletricidade ou vigiando patrimônio? A lei manda pagar 30% a mais todo mês.',
      category: 'adicionais',
    },
    {
      icon: ShieldAlert,
      title: 'Humilhações, gritos e pressão exagerada',
      description: 'Metas impossíveis, chefe xingando ou te fazendo passar vergonha na frente dos outros? Isso dá processo e indenização.',
      category: 'adicionais',
    },
    {
      icon: Heart,
      title: 'Se machucou ou adoeceu por causa do serviço',
      description: 'Dor na coluna, problema nos braços ou crise de pânico por causa do trabalho? Você tem direito a tratamento e estabilidade.',
      category: 'adicionais',
    },
  ];

  const filteredProblems = activeCategory === 'all'
    ? allProblems
    : allProblems.filter((p) => p.category === activeCategory);

  const categories = [
    { id: 'all', label: 'Todos' },
    { id: 'rescisao', label: 'Demissão e Acerto' },
    { id: 'horas', label: 'Horas Extras' },
    { id: 'registro', label: 'Sem Carteira / MEI' },
    { id: 'adicionais', label: 'Perigo e Humilhação' },
  ];

  const scrollToContact = () => {
    const el = document.getElementById('contato');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="problemas"
      data-testid="problem-types-section"
      className="py-14 sm:py-20 bg-slate-50"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Reveal delay={0.1} className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 text-blue-600 text-xs sm:text-sm font-semibold mb-3">
            <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span>Você não está sozinho</span>
          </div>
          <h2
            data-testid="problem-types-title"
            className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4 tracking-tight leading-tight"
          >
            Aconteceu alguma dessas coisas com você?
          </h2>
          <p className="text-sm sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Muita gente acha que isso é normal de empresa. Não é. A lei proíbe e a gente ajuda você a receber de volta.
          </p>
        </Reveal>

        {/* Category Filter — scroll horizontal suave no mobile */}
        <div className="flex overflow-x-auto no-scrollbar sm:flex-wrap justify-start sm:justify-center gap-2 pb-3 mb-6 sm:mb-10 -mx-4 px-4 sm:mx-0 sm:px-0">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              data-testid={`category-${cat.id}`}
              className={`px-4 py-2 sm:px-5 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all shrink-0 ${
                activeCategory === cat.id
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                  : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Problems Grid */}
        <StaggerGroup staggerDelay={0.06} startDelay={0.15} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-6">
          {filteredProblems.map((problem, index) => (
            <StaggerItem
              key={index}
              data-testid={`problem-card-${index}`}
              onClick={scrollToContact}
              whileTap={{ scale: 0.98 }}
              className="group bg-white p-5 sm:p-6 rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-blue-200 transition-all cursor-pointer"
            >
              <div className="flex sm:block items-start gap-3.5 mb-2 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                  <problem.icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug">
                  {problem.title}
                </h3>
              </div>
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                {problem.description}
              </p>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
};
